import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { FileSignature, Loader2, Download } from 'lucide-react';
import { toast } from 'sonner';
import { servicesEngagementAPI as API, LETTER_STATUS_META, fmtMoney, downloadLetterPdf } from '../lib/servicesEngagementAPI';
import { InviteActions, InviteEmailPill } from '../components/esign/InviteActions';

const fmt = (iso) => { if (!iso) return '—'; try { return new Date(iso).toLocaleDateString('en-AU'); } catch { return iso; } };

export default function EngagementLettersStorage({ embedded = false }) {
  const navigate = useNavigate();
  const [letters, setLetters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await API.listAdmin(statusFilter !== 'all' ? { status: statusFilter } : undefined);
      setLetters(res.data.letters || []);
    } catch (err) {
      toast.error('Failed to load engagement letters');
    } finally { setLoading(false); }
  };

  return (
    <div className={embedded ? 'space-y-6' : 'p-6 space-y-6'}>
      {!embedded && <div>
        <h1 className="text-3xl font-bold text-foreground" data-testid="eng-storage-title">Engagement Letters</h1>
        <p className="text-muted-foreground mt-1">All client engagement letters, their fees and signing status.</p>
      </div>}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-3 mb-4">
            <span className="text-sm text-muted-foreground">Status</span>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-56"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {Object.entries(LETTER_STATUS_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {loading ? (
            <div className="animate-pulse h-64 bg-muted rounded" />
          ) : letters.length === 0 ? (
            <div className="text-center py-12">
              <FileSignature className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" />
              <p className="text-muted-foreground">No engagement letters{statusFilter !== 'all' ? ' with this status' : ' yet'}.</p>
            </div>
          ) : (
            <Table>
              <TableHeader><TableRow className="bg-muted/50">
                <TableHead>Reference</TableHead><TableHead>Client</TableHead><TableHead>Total</TableHead>
                <TableHead>Sent</TableHead><TableHead>Signed</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {letters.map((l) => (
                  <TableRow key={l.id} className="hover:bg-muted/50" data-testid={`eng-row-${l.id}`}>
                    <TableCell className="font-medium">{l.engagement_letter_number}</TableCell>
                    <TableCell className="text-sm">{l.client_name}</TableCell>
                    <TableCell className="text-sm">{fmtMoney(l.fee_summary?.total)}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{fmt(l.sent_at)}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{fmt(l.signed_at)}</TableCell>
                    <TableCell><div className="flex flex-wrap items-center gap-1"><Badge className={(LETTER_STATUS_META[l.status] || {}).color}>{(LETTER_STATUS_META[l.status] || {}).label || l.status}</Badge>
                      {['awaiting_signature', 'partially_signed'].includes(l.esign_status) && <InviteEmailPill status={l.invite_email_status} testId={`eng-invite-status-${l.id}`} />}</div></TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-1">
                        {['awaiting_signature', 'partially_signed'].includes(l.esign_status) && <InviteActions letterId={l.id} onChanged={load} />}
                        <Button size="sm" variant="outline" data-testid={`download-letter-${l.id}`}
                          onClick={() => downloadLetterPdf(l.id, l.engagement_letter_number).catch(() => toast.error('Failed to download PDF'))}>
                          <Download className="w-4 h-4 mr-1" /> PDF
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => navigate(`/clients/${l.client_id}/engagement`)}>Manage</Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
